import React, {useCallback, useMemo} from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import MuiTooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {BarElement, CategoryScale, Chart as ChartJS, LinearScale, Title, Tooltip, Legend} from 'chart.js';
import {Bar} from 'react-chartjs-2';
import {MinimalCutSet} from '../../../../utils/minimal-cut-sets';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface OrderDistributionChartProps {
    cutSets: MinimalCutSet[];
    chartRef?: React.RefObject<ChartJS | null>;
}

const OrderDistributionChart: React.FC<OrderDistributionChartProps> = ({ cutSets, chartRef }) => {
    const distributionData = useMemo(() => {
        const counts = new Map<number, number>();
        for (const cs of cutSets) {
            counts.set(cs.order, (counts.get(cs.order) || 0) + 1);
        }
        const orders = Array.from(counts.keys()).sort((a, b) => a - b);
        return {
            labels: orders.map((o) => `${o}`),
            counts: orders.map((o) => counts.get(o)!),
            orders,
        };
    }, [cutSets]);

    const chartData = useMemo(() => ({
        labels: distributionData.labels,
        datasets: [{
            label: 'Cut sets',
            data: distributionData.counts,
            backgroundColor: distributionData.orders.map((o) =>
                o === 1 ? 'rgba(244, 67, 54, 0.7)' : 'rgba(25, 118, 210, 0.7)'
            ),
            borderColor: distributionData.orders.map((o) =>
                o === 1 ? 'rgba(244, 67, 54, 1)' : 'rgba(25, 118, 210, 1)'
            ),
            borderWidth: 1,
        }],
    }), [distributionData]);

    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            title: { display: false },
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx: { raw: unknown }): string => {
                        const count = ctx.raw as number;
                        const pct = cutSets.length > 0 ? ((count / cutSets.length) * 100).toFixed(1) : '0';
                        return `${count} cut set${count !== 1 ? 's' : ''} (${pct}%)`;
                    },
                    afterLabel: (ctx: { dataIndex: number }): string | undefined => {
                        const order = distributionData.orders[ctx.dataIndex];
                        if (order === 1) return 'Single points of failure';
                        return undefined;
                    },
                },
            },
        },
        scales: {
            x: {
                title: { display: true, text: 'Order (number of basic events)' },
            },
            y: {
                title: { display: true, text: 'Number of cut sets' },
                beginAtZero: true,
                ticks: { precision: 0 },
            },
        },
    }), [cutSets.length, distributionData.orders]);

    const handleExportCSV = useCallback((): void => {
        const header = ['Order', 'Cut Sets', 'Percentage (%)'].join(',');
        const csvRows = distributionData.orders.map((order, i) => {
            const count = distributionData.counts[i];
            const pct = cutSets.length > 0 ? ((count / cutSets.length) * 100).toFixed(1) : '0';
            return [order, count, pct].join(',');
        });
        const csv = [header, ...csvRows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `order_distribution_${new Date().toISOString().slice(0, 10)}.csv`);
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [distributionData, cutSets.length]);

    if (cutSets.length === 0) return null;

    return (
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle2">Cut Set Order Distribution</Typography>
                <MuiTooltip title="Export to CSV">
                    <IconButton size="small" onClick={handleExportCSV}>
                        <FileDownloadIcon fontSize="small" />
                    </IconButton>
                </MuiTooltip>
            </Stack>
            <Box sx={{ height: 250 }}>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                <Bar ref={chartRef as any} data={chartData} options={options} />
            </Box>
        </Box>
    );
};

export default OrderDistributionChart;
